import { useEffect, useRef, useState } from 'react';
import { REACTION_POPOVER_HOVER_DELAY, REACTION_POPOVER_HIDE_DELAY } from '../../constants';

/**
 * Custom hook for managing reaction popover hover state and animations
 *
 * Provides smooth UX for emoji reaction popovers by managing hover delays,
 * animation states, and proper cleanup of timeouts.
 *
 */
export const useReactionPopover = () => {
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = (emoji: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

    hoverTimeoutRef.current = setTimeout(() => {
      setIsAnimating(true);
      setHoveredEmoji(emoji);
    }, REACTION_POPOVER_HOVER_DELAY);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    setIsAnimating(false);
    animationTimeoutRef.current = setTimeout(() => {
      setHoveredEmoji(null);
    }, REACTION_POPOVER_HIDE_DELAY);
  };

  return {
    hoveredEmoji,
    isAnimating,
    handleMouseEnter,
    handleMouseLeave,
  };
};
